/**
 * Emitted on `EventBus` with the restaurant id once the assistant panel
 * applied or undid a change to option groups themselves (a new group, its
 * rules, texts or choices; plan P3-4). A group that no live product uses
 * changes without a realtime `catalogUpdate` (the event names products and
 * categories only), so the pages that list groups reload on this too.
 */
export const OPTION_GROUPS_CHANGED_EVENT = 'option-groups-changed';

/**
 * Emitted on `EventBus` with an `IStockChangedEvent` once the assistant
 * panel applied or undid an inventory change (plan P5-3). Stock writes send
 * no realtime `catalogUpdate` and only the quantities of the stock items
 * they moved, so the pages that show recipes and stock items follow this.
 */
export const STOCK_CHANGED_EVENT = 'stock-changed';

/** What an inventory change touched; null = not known (any may have changed). */
export interface IStockChangedEvent {
  restaurantId: number;
  /** The branch whose inventory changed. */
  branchId: number | null;
  /** The products whose recipe, or a variant's, changed. */
  itemIds: number[] | null;
  /** The stock items created, edited or moved. */
  stockItemIds: number[] | null;
}

/** The same store, and the same branch unless the change does not know its own. */
function sameBranch(
  change: IStockChangedEvent,
  target: { restaurantId: number; branchId: number }
): boolean {
  return (
    change.restaurantId === target.restaurantId &&
    (change.branchId === null || change.branchId === target.branchId)
  );
}

/**
 * Whether an inventory change may have changed what a product's stock shows
 * in a branch: its recipe (or a variant's), or a stock item its lines use.
 */
export function stockChangeConcernsProduct(
  change: IStockChangedEvent,
  target: {
    restaurantId: number;
    branchId: number;
    itemId: number;
    stockItemIds: readonly number[];
  }
): boolean {
  if (!sameBranch(change, target)) return false;
  if (change.itemIds === null || change.itemIds.includes(target.itemId))
    return true;
  return (
    change.stockItemIds === null ||
    change.stockItemIds.some((id) => target.stockItemIds.includes(id))
  );
}

/**
 * Whether an inventory change may have changed a branch's list of stock
 * items: one created, renamed, re-costed or moved.
 */
export function stockChangeConcernsStockItems(
  change: IStockChangedEvent,
  target: { restaurantId: number; branchId: number }
): boolean {
  return (
    sameBranch(change, target) &&
    (change.stockItemIds === null || change.stockItemIds.length > 0)
  );
}
